import { useEffect, useMemo, useState } from 'react'
import {
  Search, RefreshCw, FileText, MessageCircle, Filter, ChevronDown,
  CheckSquare, Square, Package, Loader2, X, CalendarDays, Building2, AlertCircle
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { getAuthHeader } from '../../../lib/edgeFunctions';
import { lignesDe, reglementDeLInscription } from '../../../lib/inscriptions/reglement'
import { useReglementDesInscriptions } from '../../../lib/inscriptions/useReglementDesInscriptions'
import { PastilleDeReglement } from '../../../components/admin/PastilleDeReglement'

interface LatestRegistrationRow {
  registration_id: string
  created_at: string
  /** Seul `cancelled` y compte encore : une commande abandonnée. */
  payment_status: string
  cancellation_status: string
  subgroup_name: string | null
  kid_id: string
  kid_nom: string
  kid_prenom: string
  parent_nom: string | null
  parent_prenom: string | null
  parent_telephone: string | null
  session_id: string
  session_periode: string | null
  session_semaine: string | null
  session_start_date: string
  session_end_date: string
  stage_title: string
  center_id: string
  center_name: string
}

type PeriodFilter = 'weekend' | '7d' | '30d' | 'all'

const LIMIT_OPTIONS = [30, 50, 100, 200]

function formatDateTime(iso: string) {
  const d = new Date(iso)
  return d.toLocaleString('fr-BE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function isWeekend(iso: string) {
  const day = new Date(iso).getDay()
  return day === 0 || day === 6
}

function lastWeekendStart(): Date {
  const now = new Date()
  const d = new Date(now)
  d.setHours(0, 0, 0, 0)
  const day = d.getDay()
  const diffToFriday = (day + 2) % 7
  d.setDate(d.getDate() - diffToFriday)
  d.setHours(17, 0, 0, 0)
  return d
}

export function LatestRegistrations() {
  const [rows, setRows] = useState<LatestRegistrationRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [limit, setLimit] = useState(30)
  const [search, setSearch] = useState('')
  const [period, setPeriod] = useState<PeriodFilter>('all')
  const [centerFilter, setCenterFilter] = useState<string>('')
  const [weekFilter, setWeekFilter] = useState<string>('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [generating, setGenerating] = useState(false)
  const [expanded, setExpanded] = useState(true)

  const loadRegistrations = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data, error: fetchError } = await supabase
        .from('registrations')
        .select(`
          id,
          created_at,
          payment_status,
          cancellation_status,
          subgroup_name,
          kid_id,
          user_id,
          activity_id,
          kids!inner(id, nom, prenom),
          users!inner(nom, prenom, telephone),
          sessions!inner(
            id, periode, semaine, start_date, end_date, archived,
            stages!inner(title),
            centers!inner(id, name, archived)
          )
        `)
        .order('created_at', { ascending: false })
        .limit(limit * 3)

      if (fetchError) throw fetchError

      const mapped: LatestRegistrationRow[] = (data || [])
        .filter((r: any) => r.sessions && !r.sessions.archived && r.sessions.centers && !r.sessions.centers.archived)
        .slice(0, limit)
        .map((r: any) => ({
          registration_id: r.id,
          created_at: r.created_at,
          payment_status: r.payment_status,
          cancellation_status: r.cancellation_status,
          subgroup_name: r.subgroup_name,
          kid_id: r.kid_id,
          kid_nom: r.kids?.nom || '',
          kid_prenom: r.kids?.prenom || '',
          parent_nom: r.users?.nom || null,
          parent_prenom: r.users?.prenom || null,
          parent_telephone: r.users?.telephone || null,
          session_id: r.sessions.id,
          session_periode: r.sessions.periode,
          session_semaine: r.sessions.semaine,
          session_start_date: r.sessions.start_date,
          session_end_date: r.sessions.end_date,
          stage_title: r.sessions.stages?.title || '',
          center_id: r.sessions.centers?.id || '',
          center_name: r.sessions.centers?.name || '',
        }))

      setRows(mapped)
      setSelected(new Set())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadRegistrations()
  }, [limit])

  // Relu à chaque chargement des lignes : « Actualiser » relit aussi les règlements.
  const registrationIds = useMemo(() => rows.map((r) => r.registration_id), [rows])
  const reglements = useReglementDesInscriptions(registrationIds)

  const centers = useMemo(() => {
    const map = new Map<string, string>()
    rows.forEach((r) => map.set(r.center_id, r.center_name))
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }))
  }, [rows])

  const weeks = useMemo(() => {
    const map = new Map<string, { semaine: string; periode: string | null; start: string; end: string }>()
    rows.forEach((r) => {
      if (!r.session_semaine) return
      const key = `${r.session_semaine}|${r.session_start_date}|${r.session_end_date}`
      if (!map.has(key)) {
        map.set(key, {
          semaine: r.session_semaine,
          periode: r.session_periode,
          start: r.session_start_date,
          end: r.session_end_date,
        })
      }
    })
    return Array.from(map.values()).sort((a, b) => a.start.localeCompare(b.start))
  }, [rows])

  const filteredRows = useMemo(() => {
    const now = Date.now()
    const weekendStart = lastWeekendStart().getTime()
    const days7 = now - 7 * 24 * 60 * 60 * 1000
    const days30 = now - 30 * 24 * 60 * 60 * 1000
    const q = search.trim().toLowerCase()

    return rows.filter((r) => {
      if (centerFilter && r.center_id !== centerFilter) return false
      if (weekFilter && r.session_semaine !== weekFilter) return false

      const createdMs = new Date(r.created_at).getTime()
      if (period === 'weekend' && createdMs < weekendStart) return false
      if (period === '7d' && createdMs < days7) return false
      if (period === '30d' && createdMs < days30) return false

      if (q) {
        const hay = [
          r.kid_nom, r.kid_prenom, r.parent_nom, r.parent_prenom,
          r.stage_title, r.center_name, r.subgroup_name,
        ].filter(Boolean).join(' ').toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [rows, period, centerFilter, weekFilter, search])

  const groupedByCenter = useMemo(() => {
    const map = new Map<string, LatestRegistrationRow[]>()
    filteredRows.forEach((r) => {
      if (!selected.has(r.registration_id)) return
      if (!map.has(r.center_id)) map.set(r.center_id, [])
      map.get(r.center_id)!.push(r)
    })
    return map
  }, [filteredRows, selected])

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAllVisible = () => {
    if (filteredRows.every((r) => selected.has(r.registration_id))) {
      setSelected(new Set())
    } else {
      setSelected(new Set(filteredRows.map((r) => r.registration_id)))
    }
  }

  const buildWhatsAppMessage = (rowsForCenter: LatestRegistrationRow[]) => {
    const header = `Nouvelles inscriptions - ${rowsForCenter[0].center_name}`
    const lines = rowsForCenter.map((r) => {
      const groupe = r.subgroup_name ? `${r.stage_title} / ${r.subgroup_name}` : r.stage_title
      return `- ${r.kid_prenom} ${r.kid_nom} - ${groupe} (parent: ${r.parent_prenom || ''} ${r.parent_nom || ''}${r.parent_telephone ? ' - ' + r.parent_telephone : ''})`
    })
    return `${header}\n\n${lines.join('\n')}`
  }

  const openWhatsAppForRow = (r: LatestRegistrationRow) => {
    const groupe = r.subgroup_name ? `${r.stage_title} / ${r.subgroup_name}` : r.stage_title
    const msg = `Nouvelle inscription\nEnfant: ${r.kid_prenom} ${r.kid_nom}\nCentre: ${r.center_name}\nGroupe: ${groupe}\nParent: ${r.parent_prenom || ''} ${r.parent_nom || ''}${r.parent_telephone ? '\nTél: ' + r.parent_telephone : ''}`
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank')
  }

  const downloadKidHealthPdf = async (r: LatestRegistrationRow) => {
    try {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-coordination-health-pdf`
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: await getAuthHeader(),
        },
        body: JSON.stringify({ registration_ids: [r.registration_id] }),
      })
      if (!resp.ok) throw new Error(await resp.text())
      const blob = await resp.blob()
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `fiche-sante-${r.kid_nom}-${r.kid_prenom}.pdf`
      a.click()
      URL.revokeObjectURL(a.href)
    } catch (err) {
      alert('Erreur PDF: ' + (err instanceof Error ? err.message : 'inconnue'))
    }
  }

  const generateCoordinationPackage = async () => {
    if (groupedByCenter.size === 0) return
    setGenerating(true)
    try {
      for (const [centerId, rowsForCenter] of groupedByCenter.entries()) {
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-coordination-health-pdf`
        const resp = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: await getAuthHeader(),
          },
          body: JSON.stringify({
            registration_ids: rowsForCenter.map((r) => r.registration_id),
            center_name: rowsForCenter[0].center_name,
          }),
        })
        if (!resp.ok) throw new Error(`Centre ${rowsForCenter[0].center_name}: ${await resp.text()}`)

        const blob = await resp.blob()
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = `coordination-${rowsForCenter[0].center_name.replace(/[^a-z0-9]/gi, '_')}.pdf`
        a.click()
        URL.revokeObjectURL(a.href)

        const msg = buildWhatsAppMessage(rowsForCenter)
        await navigator.clipboard.writeText(msg).catch(() => null)
      }
      alert('Paquets générés. Le récap texte du dernier centre est dans le presse-papier.')
    } catch (err) {
      alert('Erreur génération: ' + (err instanceof Error ? err.message : 'inconnue'))
    } finally {
      setGenerating(false)
    }
  }

  const allVisibleSelected = filteredRows.length > 0 && filteredRows.every((r) => selected.has(r.registration_id))

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between px-6 py-4 border-b border-gray-200 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <CalendarDays className="h-5 w-5 text-blue-600" />
          </div>
          <div className="text-left">
            <h2 className="text-lg font-semibold text-gray-900">Dernières inscriptions</h2>
            <p className="text-sm text-gray-500">
              Repérer les inscriptions du weekend et envoyer les fiches aux coordinations
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-500">{rows.length} chargées · {filteredRows.length} affichées</span>
          <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {expanded && (
        <div className="p-6 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un enfant ou un parent..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
              {([
                { id: 'weekend', label: 'Weekend' },
                { id: '7d', label: '7 jours' },
                { id: '30d', label: '30 jours' },
                { id: 'all', label: 'Tout' },
              ] as { id: PeriodFilter; label: string }[]).map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPeriod(p.id)}
                  className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                    period === p.id ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <select
                value={centerFilter}
                onChange={(e) => setCenterFilter(e.target.value)}
                className="pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="">Tous les centres</option>
                {centers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="relative">
              <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <select
                value={weekFilter}
                onChange={(e) => setWeekFilter(e.target.value)}
                className="pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="">Toutes les semaines</option>
                {weeks.map((w) => (
                  <option key={w.semaine} value={w.semaine}>
                    {w.semaine}{w.periode ? ` - ${w.periode}` : ''} ({w.start} → {w.end})
                  </option>
                ))}
              </select>
            </div>

            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                {LIMIT_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n} dernières</option>
                ))}
              </select>
            </div>

            <button
              onClick={loadRegistrations}
              disabled={loading}
              className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Rafraîchir</span>
            </button>
          </div>

          {selected.size > 0 && (
            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
              <div className="flex items-center space-x-2 text-sm text-blue-900">
                <Package className="h-4 w-4" />
                <span>
                  <strong>{selected.size}</strong> inscription{selected.size > 1 ? 's' : ''} sélectionnée{selected.size > 1 ? 's' : ''} sur{' '}
                  <strong>{groupedByCenter.size}</strong> centre{groupedByCenter.size > 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setSelected(new Set())}
                  className="px-3 py-1.5 text-sm text-blue-700 hover:bg-blue-100 rounded-md transition-colors"
                >
                  Désélectionner
                </button>
                <button
                  onClick={generateCoordinationPackage}
                  disabled={generating}
                  className="flex items-center space-x-2 px-4 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
                  <span>Générer paquet coordination</span>
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-start space-x-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left">
                    <button onClick={toggleAllVisible} className="text-gray-500 hover:text-gray-700">
                      {allVisibleSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                    </button>
                  </th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600 uppercase text-xs tracking-wider">Inscription</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600 uppercase text-xs tracking-wider">Enfant</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600 uppercase text-xs tracking-wider">Centre</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600 uppercase text-xs tracking-wider">Groupe</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600 uppercase text-xs tracking-wider">Semaine</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600 uppercase text-xs tracking-wider">Parent</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600 uppercase text-xs tracking-wider">Paiement</th>
                  <th className="px-3 py-3 text-right font-medium text-gray-600 uppercase text-xs tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {loading && (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                      Chargement...
                    </td>
                  </tr>
                )}
                {!loading && filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                      Aucune inscription à afficher avec ces filtres.
                    </td>
                  </tr>
                )}
                {!loading && filteredRows.map((r) => {
                  const weekend = isWeekend(r.created_at)
                  const isSelected = selected.has(r.registration_id)
                  return (
                    <tr key={r.registration_id} className={`hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50/50' : ''}`}>
                      <td className="px-3 py-3">
                        <button
                          onClick={() => toggleSelect(r.registration_id)}
                          className="text-gray-400 hover:text-blue-600"
                        >
                          {isSelected ? <CheckSquare className="h-4 w-4 text-blue-600" /> : <Square className="h-4 w-4" />}
                        </button>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="text-gray-900">{formatDateTime(r.created_at)}</div>
                        {weekend && (
                          <span className="inline-flex items-center px-1.5 py-0.5 mt-1 text-[10px] font-semibold rounded bg-orange-100 text-orange-800 border border-orange-200 uppercase tracking-wide">
                            Weekend
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="font-medium text-gray-900">{r.kid_nom} {r.kid_prenom}</div>
                      </td>
                      <td className="px-3 py-3 text-gray-700">{r.center_name}</td>
                      <td className="px-3 py-3">
                        <div className="text-gray-900">{r.stage_title}</div>
                        {r.subgroup_name && <div className="text-xs text-gray-500">{r.subgroup_name}</div>}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        {r.session_semaine && <div className="font-medium text-gray-900">{r.session_semaine}</div>}
                        {r.session_periode && <div className="text-xs text-gray-500">{r.session_periode}</div>}
                        <div className="text-xs text-gray-400 mt-0.5">
                          {r.session_start_date} → {r.session_end_date}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-gray-900">{r.parent_prenom} {r.parent_nom}</div>
                        {r.parent_telephone && <div className="text-xs text-gray-500">{r.parent_telephone}</div>}
                      </td>
                      <td className="px-3 py-3">
                        <PastilleDeReglement
                          statut={reglementDeLInscription(
                            { annulation: r.cancellation_status, commande: r.payment_status },
                            lignesDe(reglements, r.registration_id),
                          )}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end space-x-1">
                          <button
                            onClick={() => downloadKidHealthPdf(r)}
                            title="Fiche santé PDF"
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          >
                            <FileText className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openWhatsAppForRow(r)}
                            title="WhatsApp"
                            className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                          >
                            <MessageCircle className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
